
import ReactMarkdown from 'react-markdown';
import '../stylesheets/AnalisisBasico.css';
import remarkGfm from 'remark-gfm';
import React, { useState, useCallback, useEffect } from 'react';
import { FiCopy, FiInfo, FiFileText } from "react-icons/fi";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Lottie from "lottie-react";
import hourglassAnimation from "../assets/C0DvzRjMMN.json";
import rehypeRaw from 'rehype-raw';
import { HiExternalLink } from "react-icons/hi";


const Analisis1 = () => {

  const [dot, setDot] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [apiResponse, setApiResponse] = useState(null); // ← nuevo estado
  const [isLoading, setIsLoading] = useState(false);


  const stripTituloPerfilMayus = (s = '') => {
    return String(s)
      .replace(
        // Elimina '[RESPUESTA]' al inicio
        /^\[RESPUESTA\]\s*/g, 
        ''
      )
      .replace(
        // Elimina SOLO al inicio "PERFIL DE LA COMPAÑÍA" o "OUTPUT:"
        /^\s*(?:#{1,6}\s*)?(?:\*\*)?\s*(?:PERFIL\s+DE\s+LA\s+COMPA(?:Ñ|N)I?A|OUTPUT):?(?:\s*\*\*)?\s*\n?(?:\s*[-_]{3,}\s*\n?)?/iu,
        ''
      );
  };
  const formatCarrierResultadoToTSV = (resultado = []) => {
    const header = ['InsCarrier', 'Frecuencia', 'Porcentaje'].join('\t');
    const rows = resultado.map(r => [
      String(r.InsCarrier ?? '').trim(),
      String(r.Frecuencia ?? ''),
      `${Math.round(Number(r.Porcentaje ?? 0))}%`
    ].join('\t'));
    return [header, ...rows].join('\n');
  };

  const handleDownloadPDFSelectable = useCallback(async () => {
    const element = document.querySelector('.analisis1-box');
    if (!element) return alert("No encontré el contenedor del análisis.");

    try {
      // 🧩 Clonar el contenido visible (sin afectar el DOM real)
      const clone = element.cloneNode(true);

      // 1️⃣ Eliminar formularios y controles de UI
      clone.querySelectorAll(`
        form,
        .file-drop-zone,
        .selected-files,
        .browse-btn,
        .submit-btn,
        input,
        select,
        label,
        .custom-multiselect,
        .copy-btn,
        .copy-container
      `).forEach(el => el.remove());

      // 2️⃣ Eliminar títulos irrelevantes
      clone.querySelectorAll('h4, h3').forEach(h => {
        const txt = h.textContent.toLowerCase();
        if (
          txt.includes('chameleon tipo 1') ||
          txt.includes('chameleon tipo 2') ||
          txt.includes('dot info') ||
          txt.includes('validación de documentos') ||
          txt.includes('perfil de la compañía') ||
          txt.includes('historial de drivers') ||
          txt.includes('historial de carriers')
        ) {
          const next = h.nextElementSibling;
          if (next && next.tagName.toLowerCase() === 'table') next.remove();
          h.remove();
        }
      });

      // 3️⃣ Eliminar textos sueltos o marcadores vacíos
      Array.from(clone.querySelectorAll('p')).forEach(p => {
        const txt = p.textContent.trim().toLowerCase();
        if (
          txt.includes('choose a file') ||
          txt.includes('drag & drop') ||
          txt.includes('pdf format') ||
          txt.includes('validación de documentos') ||
          txt.includes('perfil de la compañía') ||
          txt.includes('historial de drivers') ||
          txt.includes('historial de carriers') ||
          txt === ':' || txt === ''
        ) {
          p.remove();
        }
      });

      // 4️⃣ Extraer “NOMBRE DEL ASEGURADO” (y removerlo del DOM)
      let aseguradoText = '';
      Array.from(clone.querySelectorAll('p')).forEach(p => {
        const txt = p.textContent.trim();
        if (txt.toLowerCase().includes('nombre del asegurado')) {
          aseguradoText = txt;
          p.remove();
        }
      });

      const workingElement = clone;

      // 🧾 Configuración PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      let yPosition = margin;

      const checkPageBreak = (needed = 20) => {
        if (yPosition > pageHeight - needed) {
          pdf.addPage();
          yPosition = margin;
        }
      };

      const cleanCellContent = (text) => {
        if (!text) return "";
        return String(text)
          .replace(/&nbsp;/gi, " ")
          .replace(/&amp;/gi, "&")
          .replace(/[^\w\sáéíóúÁÉÍÓÚñÑ.,;:/()%@#&-]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };

      // 🔹 Encabezado principal
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text(`Análisis Básico`, margin, yPosition);
      yPosition += 10;

      // 🔹 Agregar nombre del asegurado si existe
      if (aseguradoText) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(aseguradoText, margin, yPosition);
        yPosition += 8;
      }

      // 🔹 Mostrar DOT
      if (dot) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(`DOT: ${dot}`, margin, yPosition);
        yPosition += 10;
      }

      // 🔹 Recorrer secciones principales
      const sections = Array.from(
        workingElement.querySelectorAll('h4.titulo-analisis, table.data-table')
      );
      let currentTitle = '';

      for (const node of sections) {
        if (node.tagName.toLowerCase() === 'h4') {
          currentTitle = cleanCellContent(node.textContent.trim());
          checkPageBreak(25);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(14);
          pdf.text(currentTitle, margin, yPosition);
          yPosition += 8;
        } else if (node.tagName.toLowerCase() === 'table') {
          const rows = Array.from(node.querySelectorAll('tr'));
          if (rows.length === 0) continue;

          const headers = Array.from(rows[0].querySelectorAll('th'))
            .map(th => cleanCellContent(th.textContent.trim().toLowerCase()));

          // 🚫 Tablas que NO deben incluirse
          const forbiddenSets = [
            // Chameleon
            ['matched dot', 'match type', 'company name'],
            // DOT Lookup / Docket Info
            ['docket', 'dot no', 'legal name', 'dba name', 'address', 'city', 'state', 'zip'],
          ];

          const isForbiddenTable = forbiddenSets.some(set =>
            set.some(h => headers.includes(h))
          );
          if (isForbiddenTable) continue;

          // Render tabla válida
          const body = rows.slice(1).map(r =>
            Array.from(r.querySelectorAll('td')).map(td =>
              cleanCellContent(td.textContent.trim())
            )
          );

          autoTable(pdf, {
            head: [headers.map(h => h.charAt(0).toUpperCase() + h.slice(1))],
            body,
            startY: yPosition,
            margin: { left: margin, right: margin },
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fillColor: [20, 61, 140], textColor: 255, fontStyle: 'bold' },
            tableWidth: 'auto',
          });

          yPosition = pdf.lastAutoTable.finalY + 10;
        }
      }

      // 🔹 Párrafos finales limpios
      const paragraphs = Array.from(workingElement.querySelectorAll('p'));
      for (const p of paragraphs) {
        const text = cleanCellContent(p.textContent.trim());
        if (!text) continue;
        checkPageBreak(20);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        const lines = pdf.splitTextToSize(text, contentWidth);
        pdf.text(lines, margin, yPosition);
        yPosition += lines.length * 5 + 4;
      }

      // 💾 Guardar PDF
      pdf.save(`AnalisisBasico_${dot || 'resultado'}.pdf`);
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('❌ Error al generar el PDF. Intenta nuevamente.');
    }
  }, [dot]);


  const handleCopyAllExceptDotInfo = useCallback(() => {
    const root = document.querySelector('.analisis1-box');
    if (!root) return alert('No encontré el contenedor.');

    const clone = root.cloneNode(true);

    // quita DOT Info + formulario y botones
    clone
      .querySelectorAll('.dot-info, form, .copy-btn, .file-drop-zone, .selected-files')
      .forEach(n => n.remove());

    const style = `
      <style>
        body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
        h1,h2,h3,h4 { margin: 0.4rem 0 0.6rem; }
        p, li { line-height: 1.4; }
        hr { border: 0; border-top: 1px solid #e5e7eb; margin: 12px 0; }
        table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
        tr:nth-child(even) { background: #f9f9f9; }
        th { background: #f0f4ff; color: #1d4ed8; }
        .table-title-bar h4 { background:#143D8C; color:#fff; padding:10px 14px; border-radius:10px; display:inline-block; }
      </style>
    `;

    const html = style + clone.innerHTML;
    const text = clone.innerText;

    if (!window.ClipboardItem) {
      navigator.clipboard.writeText(text)
        .then(() => alert('Copiado como texto (sin formato)'))
        .catch(err => alert('Error copiando: ' + err));
      return;
    }

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html],  { type: 'text/html'  }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })
    ])
    .then(() => alert('✅ Copiado'))
    .catch(err => alert('❌ Error al copiar: ' + err));
  }, []);
  // ✔️ Detecta si hay datos reales en dotInfo (string/objeto/array)
  const hasDotInfoData = (dotInfo) => {
    if (dotInfo == null) return false;

    if (typeof dotInfo === 'string') {
      return dotInfo.trim() !== '';
    }

    if (Array.isArray(dotInfo)) {
      if (dotInfo.length === 0) return false;
      // ¿Algún objeto con al menos 1 valor no vacío?
      return dotInfo.some(
        obj => obj && Object.values(obj).some(v => v != null && String(v).trim() !== '')
      );
    }

    if (typeof dotInfo === 'object') {
      // ¿Algún valor no vacío?
      return Object.values(dotInfo).some(v => v != null && String(v).trim() !== '');
    }

    return false;
  };
  // ✔️ Normaliza a TSV en todos los casos (string/objeto/array)
  const DotInfoTable = ({ dotInfo }) => {
    let parsed = dotInfo;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed); // si viene como JSON string
      } catch {
        console.warn("dotInfo no era JSON, intentando parsear TSV");
        parsed = parseTSV(dotInfo); // 👈 conviertes TSV en objeto
      }
    }

    if (!parsed || typeof parsed !== "object") return <div>DOT Info no válido</div>;

    return (
      <div className="dot-info">
        <h4><strong>DOT Info</strong></h4>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {Object.keys(parsed).map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {Object.values(parsed).map((value, idx) => (
                  <td key={idx}>
                    {value === null || value === undefined
                      ? ""
                      : Array.isArray(value)
                      ? value.join(", ")
                      : String(value)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Función para parsear TSV/espacios a diccionario
  function parseTSV(tsvString) {
    const lines = tsvString.split("\n").filter(Boolean);
    if (lines.length < 2) return {};

    const headers = lines[0].trim().split(/\s{2,}|\t/); // separa por tabs o múltiples espacios
    const values = lines[1].trim().split(/\s{2,}|\t/);

    const dict = {};
    headers.forEach((h, i) => { dict[h.trim()] = values[i] || ""; });

    return dict;
  }


  const massageChameleon = (s = '') => {
    let out = String(s);

    // 🧹 Limpieza general
    out = out
      .replace(/^\s*\*?\*?\s*(?:chameleon|camaleon)\s*tipo\s*[12]\s*:\s*(?:si|no)(?:\s*TIENE)?\s*\*?\*?\s*$/gim, '')
      .replace(/^\s*(?:\*\*)?\s*(?:chameleon|camaleon)\s*tipo\s*(1|2)\s*:?(?:\*\*)?\s*$/gim, '\n\n### Chameleon Tipo $1:\n')
      .replace(/(?:\n+### Chameleon Tipo (1|2):\s*){2,}/g, '\n\n### Chameleon Tipo $1:\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // 🚨 Validaciones numéricas
    [
      { key: "# Crash 2 Yr", emoji: "🚨", msg: "Explica qué pasó y si está validado en Loss Run", min: 1 },
      { key: "# Injuries 2 Yr", emoji: "🚓", msg: "Debes tener un reporte policial y dar una explicación clara", min: 1 },
      { key: "# Fatalities 2 Yr", emoji: "💀", msg: "Debes tener un reporte policial y dar una explicación clara", min: 1 },
      { key: "# Basic Alerts", emoji: "⚡", msg: "Debe presentarse un Safety Plan firmado por el cliente", min: 2 },
    ].forEach(({ key, emoji, msg, min }) => {
      const pattern = new RegExp(`\\|\\s*${key}\\s*\\|([^|]*)\\|`, "i");
      out = out.replace(pattern, (match, val) => {
        const num = parseInt(val);
        if (!isNaN(num) && num >= min) {
          return `| ${key} | ${num} ${emoji} <span class="alerta-crash">${msg}</span> |`;
        }
        return match;
      });
    });

    // 🚛 Validar antigüedad unidad
    const currentYear = new Date().getFullYear();
    const modelPattern = /\|\s*Power unit Oldest model\s*\|([^|]*)\|/i;
    out = out.replace(modelPattern, (match, val) => {
      const year = parseInt(val.trim());
      if (!isNaN(year)) {
        const antiguedad = currentYear - year;
        if (antiguedad >= 20) {
          return `| Power unit Oldest model | ${year} 🚨 <span class="alerta-crash">Unidad con ${antiguedad} años — adjuntar Mechanical Statement 🧾</span> |`;
        }
      }
      return match;
    });

    // 👤 Estado del owner — alerta si NO contiene 'activo'
    const ownerPattern = /\|\s*Estado\s*del\s*owner\s*\|([^|]*)\|/i;
    out = out.replace(ownerPattern, (match, val) => {
      const texto = val
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (!texto.includes("activo")) {
        return `| Estado del owner | ${val.trim()} 🚨 <span class="alerta-owner">El owner no está activo — debe explicar la situación y adjuntar CDL o MVR en el sometimiento </span> |`;
      }
      return match;
    });

    // 🧾 Validación de inspecciones "SI TIENE ..."
    const inspeccionPattern = /\|\s*(?:¿?Tiene\s+Inspecciones\??(?:\s*\(Carrier\s*Software\))?)\s*\|([^|]*)\|/i;
    out = out.replace(inspeccionPattern, (match, val) => {
      const textoOriginal = val.trim();
      const texto = textoOriginal
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (/\b(si\s*tiene|yes\s*has)\b/.test(texto)) {
        const alerta = `<span class="alerta-inspeccion"> Justificar por qué esas unidades no van a ir en ese nuevo término. <a href="https://cmvid.nisrinc.com/CMV_ID/CMV_ID.asp" target="_blank" rel="noopener noreferrer" class="alerta-link">Valida los VINs aquí 🔍</a></span> `;

        // 🔹 Inserta la alerta justo antes de "SI TIENE" (manteniendo el resto del texto intacto)
        const textoConAlerta = textoOriginal.replace(/(si\s*tiene)/i, `${alerta}$1`);

        return `| ¿Tiene Inspecciones? (Carrier Software) | ${textoConAlerta} |`;
      }

      return match;
    });

    return out;
  };


    
  const TableOrMarkdown = ({
    text = '',
    title,
    className,
    scroll = false,
    headerDelimiter = 'auto',
    rowDelimiter = 'auto',
    customTitleClass,
    scrollForTitles = [],
  }) => {
    const stateLinks = {
      FL: "http://search.sunbiz.org/Inquiry/CorporationSearch/ByName",
      TX: "https://mycpa.cpa.state.tx.us/coa/",
      CA: "https://bizfileonline.sos.ca.gov/search/business",
      PA: "https://file.dos.pa.gov/search/business",
      AZ: "https://ecorp.azcc.gov/AzAccount?sessionExpired=False",
      IL: "https://apps.ilsos.gov/businessentitysearch/",
      GA: "https://ecorp.sos.ga.gov/BusinessSearch",
    };

    let clean = String(text).trim();
    clean = clean.replace(/(\*\*[^:\n]+:\*\*)(?=\s*\|)/g, '$1\n\n');
    clean = clean
      .normalize("NFKD")
      .replace(/[\u0400-\u04FF]/g, ''); 

    if (!clean) return null;

    const flattenText = (nodes) => {
      const arr = React.Children.toArray(nodes);
      return arr.map((n) => {
        if (typeof n === 'string' || typeof n === 'number') return String(n);
        if (React.isValidElement(n)) return flattenText(n.props?.children);
        return '';
      }).join('');
    };

    const normalize = (s) =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}\s:()\-]/gu, '')
        .replace(/\s+/g, ' ')
        .trim();

    const shouldWrap = (sectionText) => {
      if (!sectionText) return false;
      const norm = normalize(sectionText);
      return scrollForTitles.some(t => norm.includes(normalize(t)));
    };

    const decoratedHeadings = [
      /^(historial de carriers)$/i,
      /^(historial de drivers)$/i,
      /^(historial de loss runs)$/i,
      /^(chameleon tipo 1)$/i,
      /^(chameleon tipo 2)$/i,
    ];

    const looksLikeMdTable = /\|.+\|/.test(clean) && /(\n|\r)\s*\|?-{3,}/.test(clean);

    if (looksLikeMdTable && headerDelimiter === 'auto' && rowDelimiter === 'auto') {
      let currentSection = '';

      const md = (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h3({ children, ...props }) {
              const plain = flattenText(children).trim();
              currentSection = plain;
              const matchDecorated = decoratedHeadings.some(r => r.test(plain));
              const base = 'md-section-title';
              return (
                <h3
                  {...props}
                  className={`${base} ${matchDecorated ? 'titulo-analisis' : ''} ${props.className || ''}`.trim()}
                >
                  {children}
                </h3>
              );
            },
            td({ children, node, ...props }) {
              const val = flattenText(children).trim();
              const valUpper = val.toUpperCase();

              // 🔹 Agregar enlace de Secretaría de Estado si aplica
              if (stateLinks[valUpper]) {
                return (
                  <td {...props}>
                    <a
                      href={stateLinks[valUpper]}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Verificar en Secretaría de Estado (${valUpper})`}
                      style={{
                        color: '#143D8C',
                        fontWeight: 600,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <span style={{ color: '#000' }}>{valUpper}</span>
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: '#143D8C',
                          textDecoration: 'underline',
                        }}
                      >
                        Verificar en Secretaría
                      </span>
                      <HiExternalLink size={15} />
                    </a>
                  </td>
                );
              }

              return <td {...props}>{children}</td>;
            },
            table({ children, ...props }) {
              const tbl = <table className="data-table" {...props}>{children}</table>;

              const normalized = (currentSection || '')
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, '');

              const isPerfil = normalized.includes('perfil de la compania');
              return isPerfil ? tbl : <div className="table-scroll">{tbl}</div>;
            },
          }}
        >
          {clean}
        </ReactMarkdown>
      );

      return (
        <div className={className}>
          {title && <h4 className={customTitleClass}>{title}</h4>}
          {md}
        </div>
      );
    }

    // Fallback TSV
    const pickSplitter = (mode, sampleRows) => {
      if (mode === 'whitespace') return /\s+/;
      if (mode === 'spaces2') return /\s{2,}/;
      if (mode === 'tsv') return /\t+/;
      return sampleRows.some(r => r.includes('\t')) ? /\t+/ : /\s{2,}/;
    };

    const rowsRaw = clean.split(/\r?\n/).map(r => r.trimEnd()).filter(Boolean);
    if (rowsRaw.length === 0) return null;

    const headSplit = pickSplitter(headerDelimiter, rowsRaw);
    let header = rowsRaw[0].split(headSplit).filter(Boolean).map(s => s.trim());
    header = header.map(h => /^inscarrier$/i.test(h) ? 'Carrier sugerido' : h);
    const expectedCols = header.length;

    const bodyLines = rowsRaw.slice(1);
    const combinedRows = [];
    let currentRow = '';
    for (let line of bodyLines) {
      if (!currentRow) {
        currentRow = line;
      } else {
        const cells = currentRow.split(pickSplitter(rowDelimiter, [currentRow]));
        if (cells.length < expectedCols) {
          currentRow += ' ' + line;
        } else {
          combinedRows.push(currentRow);
          currentRow = line;
        }
      }
    }
    if (currentRow) combinedRows.push(currentRow);

    const body = combinedRows
      .map(row => {
        const cells = row.split(pickSplitter(rowDelimiter, [row])).map(s => s.trim());
        if (cells.length < expectedCols) return cells.concat(Array(expectedCols - cells.length).fill(''));
        if (cells.length > expectedCols) {
          return [
            cells.slice(0, cells.length - (expectedCols - 1)).join(' '),
            ...cells.slice(cells.length - (expectedCols - 1))
          ];
        }
        return cells;
      });

    const tableEl = (
      <table className={`data-table ${scroll ? 'data-table--wide' : ''}`}>
        <thead>
          <tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((cells, i) => (
            <tr key={i}>
              {cells.map((c, j) => {
                const val = String(c || '').trim().toUpperCase();
                if (stateLinks[val]) {
                  return (
                    <td key={j}>
                      <a
                        href={stateLinks[val]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#143D8C',
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ color: '#000' }}>{val}</span>
                        <span
                          style={{
                            fontSize: '0.85rem',
                            color: '#143D8C',
                            textDecoration: 'underline',
                          }}
                        >
                          Verificar en Secretaría
                        </span>
                        <HiExternalLink size={15} />
                      </a>
                    </td>
                  );
                }
                return <td key={j}>{c}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );

    return (
      <div className={className}>
        {title && <h4 className={customTitleClass}>{title}</h4>}
        {scroll ? <div className="table-scroll">{tableEl}</div> : tableEl}
      </div>
    );
  };





  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const processFiles = useCallback((files) => {
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    setFiles(pdfFiles);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dot.trim()) return alert('El campo DOT es obligatorio');

    setApiResponse(null);      // ← NUEVO: limpia la respuesta previa
    setIsLoading(true);        // ← NUEVO: activa el cargando
    try {
      const formData = new FormData();
      formData.append('dot_number', dot);
      if (files && files.length > 0) {
        files.forEach((file) => formData.append('rutas_pdfs', file));
      }

      const response = await fetch('https://carrierprobapi.intruckscorp.com/Top_analizar_por_dot', {
        method: 'POST',
        headers: { accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error en la solicitud: ${response.statusText}`);
      }

      const data = await response.json();
      setApiResponse(data);
    } catch (error) {
      console.error('Error enviando el formulario:', error);
      alert('Error enviando el formulario: ' + error.message);
    } finally {
      setIsLoading(false);     // ← NUEVO: desactiva el cargando
    }
  };
  return (
    <div className="right-section">
      <div className="analisis1-box">
      <div className="title-row">
        <h1>Análisis Básico</h1>

        <button
          type="button"
          className={`info-chip ${showInfo ? 'open' : ''}`}
          onClick={() => setShowInfo(v => !v)}
          aria-label="¿Qué es Análisis Básico?"
          aria-expanded={showInfo}
        >
          <FiInfo size={18} />
        </button>

        <div className={`info-tooltip ${showInfo ? 'show' : ''}`} role="tooltip">
          Este análisis entrega un <strong>perfil general</strong> usando solo el App Quote. 
          Verás: validación básica de documentos, un resumen del perfil (YIB, radio, unidades, drivers) 
          y <strong>carriers sugeridos</strong> según Carrier Software, además del historial reciente. 
          Sólo lo debes aplicar si <strong> NO </strong> tienes aún Loss runs y MVRs
        </div>
      </div>

        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label><strong>DOT*</strong></label>
            <input
              type="text"
              value={dot}
              onChange={(e) => setDot(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label className="label-with-tooltip">
              <strong>Subir Documentos (PDF)</strong>
              <span className="mini-tooltip" role="tooltip">
                <strong>Sólo tienes que subir: </strong>  
                • App Quote  
                (si no la tienes no es obligatorio)

              </span>
            </label>

            <div
              className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="file-drop-content">
                <svg className="upload-icon" viewBox="0 0 24 24">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
                <p>Choose a file or drag & drop it here</p>
                <p className="file-formats">PDF format, up to 50MB</p>
              </div>
              <input
                type="file"
                id="file-upload"
                accept="application/pdf"
                onChange={handleFileChange}
                multiple
                className="file-input"
              />
              <label htmlFor="file-upload" className="browse-btn">
                Browse File
              </label>
            </div>
            
            {files.length > 0 && (
              <div className="selected-files">
                <h4>Selected files:</h4>
                <ul>
                  {files.map((file, index) => (
                    <li key={index}>
                      <span>{file.name}</span>
                      <button 
                        type="button" 
                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                        className="remove-btn"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? 'Cargando…' : 'Calcular'}
          </button>

          {isLoading && (
            <div className="overlay-loader">
              <Lottie
                animationData={hourglassAnimation}
                loop={true}
                style={{ width: 320, height: 320 }}
              />
              <h2 className="loading-title">
                Espéranos 1 minuticos...
                <br />
                <span className="highlight">Estamos perfilando tu cliente 🚛</span>
              </h2>
    
            </div>
          )}


        </form>



      {apiResponse?.perfil_analizado && (
        <TableOrMarkdown
          text={stripTituloPerfilMayus(massageChameleon(apiResponse.perfil_analizado))}
          title="Resultado Análisis Básico"
          className="perfil-analizado"
          customTitleClass="titulo-analisis"
          scrollForTitles={[
            'Historial de Drivers',
            'Historial de Loss Runs',
            'Chameleon Tipo 1',
            'Chameleon Tipo 2',
            'Historial de Carriers',
          ]}
        />
      )}

      {/* DOT Info (normalizado a TSV + scroll) */}
      {apiResponse?.dot_info && (
        <DotInfoTable dotInfo={apiResponse.dot_info} />
      )}



      {/* Carrier Probabilities */}
      {(() => {
        // Caso normal: el backend ya devuelve la tabla como texto
        if (apiResponse?.carrier_probabilities) {
          return (
            <TableOrMarkdown
              text={apiResponse.carrier_probabilities}
              title="Carriers Sugeridos Según Análisis de Carrier Software"
              className="carrier-probabilities-3d table"
              headerDelimiter="spaces2"
              rowDelimiter="spaces2"
              customTitleClass="titulo-analisis"   // ⬅️ añade esto
            />
          );
        }

        // Caso sin adjuntos: usar apiResponse.resultado (array) para construir la tabla
        if (Array.isArray(apiResponse?.resultado) && apiResponse.resultado.length > 0) {
          const tsv = formatCarrierResultadoToTSV(apiResponse.resultado);
          return (
            <TableOrMarkdown
              text={tsv}
              title="Carrier Probabilities"
              className="carrier-probabilities-3d table"
              headerDelimiter="tsv"
              rowDelimiter="tsv"
              customTitleClass="titulo-analisis"   // ⬅️ y aquí también
            />
          );
        }

        return null;
      })()}

      {/* Botón para copiar */}
      {apiResponse && (
        <div className="copy-container">
          <button type="button" onClick={handleCopyAllExceptDotInfo} className="copy-btn">
            <FiCopy style={{ marginRight: 6 }} />
            Copiar resultado
          </button>

          <button
            type="button"
            onClick={handleDownloadPDFSelectable}
            className="copy-btn copy-btn--green"   // ⬅️ ambas clases
            style={{ marginLeft: 8 }}
          >
            Descargar PDF
          </button>
        </div>
      )}

      </div>
    </div>


  );
};

export default Analisis1;
